import React, { ChangeEvent, useEffect, useState, useCallback } from "react";
import AgentsService from "../../services/AgentsService/AgentsService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import { normalizeAgentProperties } from "../../utils/NormalizationUtil.ts";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import AgentsDataGrid from "../../components/DataTables/AgentsDataGrid.tsx";
import { Agent } from "../../types/agent.ts";
import ConfirmationModal from "../../components/Modals/ConfirmationModal.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const NEW_AGENT_ENTRY_OPTION = { "0": "Нов запис" };
const NEW_AGENT_VALUE = "0";
const SUCCESS_MESSAGE = "Успешен запис";
const ERROR_MESSAGE = "Неуспешен запис";
const DEFAULT_AGENT_STATE: Agent = {
  agentID: "0",
  agentName: "",
  personalIdentificationNumber: "",
  idCardNumber: "",
  phone: "",
  address: "",
  otherData: "",
  isActive: "0",
};
const FIELD_AGENT_SELECT = "consultantSelect";
const FIELD_IS_ACTIVE = "isActive";
const ACTIVE_STATUS = { YES: 1, NO: 0 };
const NEW_AGENT_ID = 0;

const CommercialConsultants = () => {
  const { jwt } = useAuth();
  const [agents, setAgents] = useState<{ [key: string]: string }>({});
  const [agentsList, setAgentsList] = useState<Agent[]>([]);
  const [initialAgentsList, setInitialAgentsList] = useState<Agent[]>([]);
  const [consultantData, setConsultantData] =
    useState<Agent>(DEFAULT_AGENT_STATE);
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const [isNewAgent, setIsNewAgent] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>("");
  const [originalAgentNameOnLoad, setOriginalAgentNameOnLoad] = useState<
    string | null
  >(null);

  const displayAlert = (message: string, type: FadeAlertType) => {
    setAlert(message);
    setAlertType(type);
  };

  const fetchAgentsData = async () => {
    try {
      setLoading(true);
      const agentsRes = await AgentsService.getAllAgents(jwt);
      const agentsListResponse = await AgentsService.getAgentsList(jwt);
      setAgents({
        ...NEW_AGENT_ENTRY_OPTION,
        ...(agentsRes as { [key: string]: string }),
      });
      const normalizedAgentsList = agentsListResponse.map((agent): Agent => {
        const normalizedAgent = normalizeAgentProperties(agent);
        normalizedAgent.isActive =
          normalizedAgent.isActive === 1 ? true : false;
        return normalizedAgent;
      });
      setAgentsList(normalizedAgentsList);
      if (initialAgentsList.length === 0)
        setInitialAgentsList(normalizedAgentsList);
    } catch (err: any) {
      displayAlert(err.message, FadeAlertType.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgentsData();
  }, [jwt]);

  const handleAgentSelectChange = useCallback(
    async (value: string) => {
      if (value === NEW_AGENT_VALUE) {
        resetAgentData();
        setAgentsList(initialAgentsList);
        setOriginalAgentNameOnLoad(null);
      } else {
        try {
          const agentData = await AgentsService.getAgentById(jwt, value);
          const normalizedAgentData: Agent = normalizeAgentProperties(
            agentData as object
          );
          setConsultantData(normalizedAgentData);
          setIsNewAgent(false);
          setOriginalAgentNameOnLoad(normalizedAgentData.agentName);
        } catch (err: any) {
          displayAlert(err.message, FadeAlertType.error);
        }
      }
    },
    [initialAgentsList, jwt]
  );

  const resetAgentData = () => {
    setConsultantData(DEFAULT_AGENT_STATE);
    setIsNewAgent(true);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setConsultantData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewAgent) {
      await executeSave();
    } else {
      const currentAgentName = consultantData.agentName;
      let message = "";
      if (
        originalAgentNameOnLoad &&
        currentAgentName !== originalAgentNameOnLoad
      ) {
        message = `Ще промените ${originalAgentNameOnLoad} на ${currentAgentName}`;
      } else if (originalAgentNameOnLoad) {
        message = `Ще промените данни за ${originalAgentNameOnLoad}`;
      } else {
        message = "Ще промените данни за избрания агент";
      }
      setConfirmModalMessage(message);
      setShowConfirmModal(true);
    }
  };

  const executeSave = async () => {
    try {
      const agentDataToSave = normalizeAgentProperties(consultantData, true);
      const payload = {
        agentData: {
          ...agentDataToSave,
          IS_ACTIVE: consultantData.isActive
            ? ACTIVE_STATUS.YES
            : ACTIVE_STATUS.NO,
          AID: isNewAgent ? NEW_AGENT_ID : consultantData.agentID,
        },
      };
      const response = await AgentsService.saveAgent(jwt, payload);
      if (response) {
        displayAlert(SUCCESS_MESSAGE, FadeAlertType.success);
        resetAgentData();
        fetchAgentsData();
      } else {
        throw new Error(ERROR_MESSAGE);
      }
    } catch (err: any) {
      displayAlert(err.message, FadeAlertType.error);
    } finally {
      setShowConfirmModal(false);
    }
  };

  return (
    <>
      <Breadcrumb pageName="Рекламни консултанти" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />
        <SectionHeader title="Рекламни консултанти" />
        <form onSubmit={handleSave} className="p-3">
          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            {agents && (
              <div>
                <label className={labelClass}>Агент</label>
                <SelectGroup
                  value={NEW_AGENT_VALUE}
                  placeholder="Изберете агент"
                  options={agents}
                  onChange={(value) => handleAgentSelectChange(value)}
                  name={FIELD_AGENT_SELECT}
                />
              </div>
            )}
            <div className="lg:col-span-3">
              <label className={labelClass}>Име Презиме Фамилия</label>
              <input
                type="text"
                name="agentName"
                value={consultantData.agentName}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mb-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>ЕГН</label>
              <input
                type="text"
                name="personalIdentificationNumber"
                value={consultantData.personalIdentificationNumber}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Лична карта №</label>
              <input
                type="text"
                name="idCardNumber"
                value={consultantData.idCardNumber}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Телефон</label>
              <input
                type="text"
                name="phone"
                value={consultantData.phone}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Адрес</label>
              <input
                data-no-decimal-guard=""
                type="text"
                name="address"
                value={consultantData.address}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>
          </div>

          <div className="mb-2">
            <label className={labelClass}>Други данни</label>
            <textarea
              data-no-decimal-guard=""
              name="otherData"
              value={consultantData.otherData}
              onChange={handleInputChange}
              rows={2}
              className={inputClass + " resize-none"}
            />
          </div>

          <div className="grid grid-cols-3 items-center">
            <div className="flex justify-start">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  name={FIELD_IS_ACTIVE}
                  checked={!!consultantData.isActive}
                  onChange={handleInputChange}
                  className="h-3 w-3 rounded"
                />
                <span className="text-xs font-medium text-black dark:text-white">
                  Действащ
                </span>
              </label>
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                className="bg-primary flex w-1/2 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
              >
                Запис
              </button>
            </div>

            <div />
          </div>
        </form>
      </div>

      {Object.keys(agentsList).length > 0 && (
        <AgentsDataGrid agentsList={agentsList} isLoading={loading} />
      )}

      <ConfirmationModal
        isOpen={showConfirmModal}
        message={confirmModalMessage}
        onConfirm={executeSave}
        onCancel={() => setShowConfirmModal(false)}
      />
    </>
  );
};

export default CommercialConsultants;
